import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { getAuth, signOut } from 'firebase/auth'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useDispatch, useSelector } from 'react-redux'
import App from '@/App'
import BondTraderPromoBanner from '@/components/Layouts/BondTraderPromoBanner'
import ChallengeStatusBanner from '@/components/Layouts/ChallengeStatusBanner'
import ConnectionHealthBanner from '@/components/Layouts/ConnectionHealthBanner'
import DemoEnvironmentBanner from '@/components/Layouts/DemoEnvironmentBanner'
import { MobileMenu } from '@/components/Layouts/MobileMenu'
import { RouterDevtoolsPanel } from '@/components/Layouts/RouterDevtoolsPanel'
import { Logger } from '@/components/Logger'
import Portals from '@/components/Portals'
import { publicConfig } from '@/config/publicConfig'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import useSignOutAndClearStore from '@/hooks/useSignOutAndClearStore'
import { getUserSelfQueryKey, useGetUserSelf, useVerifyUser } from '@/hooks/v1'
import { ErrorFallBack } from '@/main'
import { QrPromoProvider } from '@/providers/QrPromoProvider'
import { SettingsProvider } from '@/providers/SettingsProvider'
import { ShortcutProvider } from '@/providers/ShortcutProvider'
import { queryClient } from '@/services/api'
import type { IRootState } from '@/store'
import { toggleLayout } from '@/store/themeConfigSlice'
import { createNotification, NotificationStatus, NotificationType } from '@/utils/notificationUtils'
import Footer from './Footer'
import Header from './Header'

const attemptedBackendVerifyUserIds = new Set<string>()

function useVerifyCurrentUserOnce() {
  const { data: userSelf } = useGetUserSelf()
  const { mutateAsync: verifyUser } = useVerifyUser()

  useEffect(() => {
    const userId = userSelf?.data?.id
    const verifiedAt = userSelf?.data?.verified_at

    if (!userId || verifiedAt || attemptedBackendVerifyUserIds.has(userId)) {
      return
    }

    attemptedBackendVerifyUserIds.add(userId)

    verifyUser({ path: { user_id: userId } })
      .then(() => queryClient.invalidateQueries({ queryKey: getUserSelfQueryKey() }))
      .catch((error) => {
        Logger.error('DefaultLayout: backend user verification failed', error)
      })
  }, [userSelf?.data?.id, userSelf?.data?.verified_at, verifyUser])
}

const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const

const useOnIdle = (timeoutMs: number, onIdle: () => void) => {
  const onIdleRef = useRef(onIdle)

  useEffect(() => {
    onIdleRef.current = onIdle
  }, [onIdle])

  useEffect(() => {
    if (typeof window === 'undefined') return

    let hasTriggered = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const triggerIdle = () => {
      if (hasTriggered) return
      hasTriggered = true
      onIdleRef.current()
    }

    const resetIdleTimer = () => {
      if (hasTriggered) return
      if (timer) {
        clearTimeout(timer)
      }
      timer = setTimeout(triggerIdle, timeoutMs)
    }

    IDLE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true })
    })

    resetIdleTimer()

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
      IDLE_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer)
      })
    }
  }, [timeoutMs])
}

const DefaultLayout = () => {
  const themeConfig = useSelector((state: IRootState) => state.themeConfig)
  const dispatch = useDispatch()
  const { isTablet } = useBreakpoints()
  const [showLoader, setShowLoader] = useState(true)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const pathname = useLocation({ select: (location) => location.pathname })
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [pathname, isTablet])
  const signOutAndClearStore = useSignOutAndClearStore()
  useVerifyCurrentUserOnce()
  const inactivityTimeoutMs = useMemo(() => {
    const timeoutValue = publicConfig.app.userInactivityTimeoutMs
    return Number.isFinite(timeoutValue) && timeoutValue > 0 ? timeoutValue : 1000 * 60 * 60 * 48
  }, [])

  const isError = false

  const handleIdleLogout = useCallback(() => {
    signOutAndClearStore()
      .catch((error) => {
        Logger.error('Error signing out', error)
      })
      .then(() => {
        createNotification(
          'User Inactivity',
          'User logged out due to inactivity',
          NotificationType.SIMPLE,
          NotificationStatus.INFO,
        )
      })
  }, [signOutAndClearStore])

  useOnIdle(inactivityTimeoutMs, handleIdleLogout)

  useEffect(() => {
    dispatch(toggleLayout('full'))
    //dispatch(toggleLayout('boxed-layout'))

    const screenLoader = document.getElementsByClassName('screen_loader')
    if (screenLoader?.length) {
      screenLoader[0].classList.add('animate__fadeOut')
      setTimeout(() => {
        setShowLoader(false)
      }, 200)
    }
  }, [])

  const reloadAfterInterval = useCallback(() => {
    const auth = getAuth()
    auth.tenantId = publicConfig.firebase.tenantId

    signOut(auth).catch((error) => {
      Logger.error('reloadAfterInterval - Error signing out', error)
    })

    setTimeout(() => {
      window.location.reload()
    }, 10000) // 10 seconds
  }, [])

  if (isError) {
    return (
      <div id="default-layout" className={'w-full h-full relative flex-row'}>
        <div className=" h-fit flex items-center justify-center">
          <div className="relative text-center font-semibold ">
            <img
              src="/assets/images/404.svg"
              alt="404"
              className="mx-auto w-full max-w-[630px] object-cover"
            />
            <p className="mt-5 text-base dark:text-white">API is down - try again later!</p>
            <a href={publicConfig.api.baseUrl} className="text-primary visited:text-secondary ...">
              {publicConfig.api.baseUrl}
            </a>

            <Link
              to="/"
              className="font-bold btn btn-gradient mx-auto !mt-7 w-max border-0 uppercase shadow-none"
            >
              Home
            </Link>
          </div>
        </div>
        <div className={'w-full z-50 absolute bottom-0'}>
          <Footer isApiAvailable={false} />
        </div>
      </div>
    )
  }

  return (
    <QrPromoProvider>
      <App>
        <SettingsProvider>
          {/*<Setting />*/}
          {showLoader && (
            <div className="screen_loader fixed inset-0 bg-black-trueBlack z-[60] grid place-content-center animate__animated">
              <svg
                width="64"
                height="64"
                viewBox="0 0 135 135"
                xmlns="http://www.w3.org/2000/svg"
                fill="#4361ee"
              >
                <path d="M67.447 58c5.523 0 10-4.477 10-10s-4.477-10-10-10-10 4.477-10 10 4.477 10 10 10zm9.448 9.447c0 5.523 4.477 10 10 10 5.522 0 10-4.477 10-10s-4.478-10-10-10c-5.523 0-10 4.477-10 10zm-9.448 9.448c-5.523 0-10 4.477-10 10 0 5.522 4.477 10 10 10s10-4.478 10-10c0-5.523-4.477-10-10-10zM58 67.447c0-5.523-4.477-10-10-10s-10 4.477-10 10 4.477 10 10 10 10-4.477 10-10z">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 67 67"
                    to="-360 67 67"
                    dur="2.5s"
                    repeatCount="indefinite"
                  />
                </path>
                <path d="M28.19 40.31c6.627 0 12-5.374 12-12 0-6.628-5.373-12-12-12-6.628 0-12 5.372-12 12 0 6.626 5.372 12 12 12zm30.72-19.825c4.686 4.687 12.284 4.687 16.97 0 4.686-4.686 4.686-12.284 0-16.97-4.686-4.687-12.284-4.687-16.97 0-4.687 4.686-4.687 12.284 0 16.97zm35.74 7.705c0 6.627 5.37 12 12 12 6.626 0 12-5.373 12-12 0-6.628-5.374-12-12-12-6.63 0-12 5.372-12 12zm19.822 30.72c-4.686 4.686-4.686 12.284 0 16.97 4.687 4.686 12.285 4.686 16.97 0 4.687-4.686 4.687-12.284 0-16.97-4.685-4.687-12.283-4.687-16.97 0zm-7.704 35.74c-6.627 0-12 5.37-12 12 0 6.626 5.373 12 12 12s12-5.374 12-12c0-6.63-5.373-12-12-12zm-30.72 19.822c-4.686-4.686-12.284-4.686-16.97 0-4.686 4.687-4.686 12.285 0 16.97 4.686 4.687 12.284 4.687 16.97 0 4.687-4.685 4.687-12.283 0-16.97zm-35.74-7.704c0-6.627-5.372-12-12-12-6.626 0-12 5.373-12 12s5.374 12 12 12c6.628 0 12-5.373 12-12zm-19.823-30.72c4.687-4.686 4.687-12.284 0-16.97-4.686-4.686-12.284-4.686-16.97 0-4.687 4.686-4.687 12.284 0 16.97 4.686 4.687 12.284 4.687 16.97 0z">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 67 67"
                    to="360 67 67"
                    dur="8s"
                    repeatCount="indefinite"
                  />
                </path>
              </svg>
            </div>
          )}
          <Header setIsMobileMenuOpen={setIsMobileMenuOpen} isMobileMenuOpen={isMobileMenuOpen} />
          <DemoEnvironmentBanner />
          <BondTraderPromoBanner />
          <ChallengeStatusBanner />
          <ConnectionHealthBanner />
          <div
            id="main"
            className="flex-1 flex flex-col text-black dark:text-white-dark bg-black-trueBlack mb-[calc(2rem+env(safe-area-inset-bottom))] md:mb-[24px] overflow-y-auto"
            style={{ scrollbarGutter: 'stable both-edges' }}
          >
            {isTablet && isMobileMenuOpen && (
              <MobileMenu setIsMobileMenuOpen={setIsMobileMenuOpen} />
            )}
            <Suspense>
              <div
                className={`${themeConfig.animation} animate__animated ${isTablet && isMobileMenuOpen ? 'min-h-0' : 'h-full'}`}
              >
                <ErrorBoundary
                  onError={(error) => {
                    Logger.error('Error in DefaultLayout', error)
                    reloadAfterInterval()
                  }}
                  FallbackComponent={ErrorFallBack}
                  onReset={() => signOutAndClearStore()}
                >
                  <ShortcutProvider>
                    <Outlet />
                    <RouterDevtoolsPanel initialIsOpen={false} position="bottom-left" />
                  </ShortcutProvider>
                </ErrorBoundary>
              </div>
            </Suspense>
          </div>
          <Footer isApiAvailable={true} />
          <Portals />
        </SettingsProvider>
      </App>
    </QrPromoProvider>
  )
}

export default DefaultLayout
